import { ColumnMeta } from '@api';
import { TableCell, TableHeader } from '@design/components/table';
import { TanStackTableRoot } from '@design/components/tanstack-table/TanStackTable';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { CompetitorsTableData } from '@terminal/widgets/knowledge-graph-widget/hooks/useCompetitorsData';
import { SubTabsType } from '@terminal/widgets/theme-widget/ThemeWidget';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LoaderCell } from '../../theme-exposure-tab/theme-exposure-table/loader-cell/LoaderCell';
import { RankingMethodologySection } from '../../theme-exposure-tab/theme-exposure-table/ranking-methodology-section/RankingMethodologySection';
import styles from './CompetitorsTable.module.scss';
import { getColumns } from './getColumns';

interface CompetitorsTableProps {
  tableData: CompetitorsTableData[];
  columnsMeta: ColumnMeta[];
  isError: boolean;
  isFetching: boolean;
}

export const CompetitorsTable = ({
  tableData,
  columnsMeta,
  isError,
  isFetching,
}: CompetitorsTableProps) => {
  const { t } = useTranslation(['widget']);
  const { setQueryParams } = useQueryParams();

  const columns = useMemo(
    () => getColumns({ columnsMeta, t }),
    [columnsMeta, t]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualFiltering: true,
  });

  if (isError) {
    throw new Error('KG - Screener data fetching error');
  }

  const headerGroups = table.getHeaderGroups();
  const { rows } = table.getRowModel();

  return (
    <TanStackTableRoot className={styles.styledTanStackTableRoot}>
      <thead>
        {headerGroups.map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header, headerIdx) => (
              <TableHeader
                key={header.id}
                className={styles.styledTableHeader}
                data-table-header
                alignment={headerIdx > 2 ? 'end' : 'start'}
                isHoverEnabled={false}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </TableHeader>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {isFetching ? (
          <LoaderCell numberOfRows={3} />
        ) : (
          rows
            .filter(row => row.original.hasData)
            .map(row => (
              <tr key={row.id} role="button" tabIndex={0}>
                {row.getVisibleCells().map((cell, idx) => {
                  return (
                    <TableCell
                      key={cell.id}
                      alignment={idx > 2 ? 'end' : 'start'}
                      isNumber={idx > 2}
                      onClick={() => {
                        setQueryParams({
                          [queryKeys.activeDrawer]:
                            queryValues.competitorsDrawer,
                          [queryKeys.activeDrawerTab]:
                            SubTabsType.CompetitorOverview,
                          [queryKeys.competitorTag]: row.original.entity?.tag,
                        });
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </tr>
            ))
        )}
        <tr data-testid="footer-row">
          <TableCell
            className={styles.footerRow}
            data-footer-row
            colspan={table.getHeaderGroups()[0].headers.length}
            isNumber={false}
          >
            <RankingMethodologySection rankingType="competitorsToTheme" />
          </TableCell>
        </tr>
      </tbody>
    </TanStackTableRoot>
  );
};
